import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DetailedOrderData } from "~/domains/orders/types/modelsV2";
import { Stack, styled, Button, Menu, MenuItem, Paper, Typography } from "~/common/mui";
import { downloadFile } from "~/common/utils/ApiHelper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import type { InvoiceInfoData } from "~/domains/orders/types/models";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

interface OrderDetailsHeaderProps {
  data: DetailedOrderData;
}

const OrderDetailsHeaderV2: React.FC<OrderDetailsHeaderProps> = ({ data }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState({
    invoice: false,
    deliveryNote: false,
    orderConfirmation: false,
  });

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDocumentDownload = async (type: "invoice" | "deliveryNote" | "orderConfirmation") => {
    handleClose();

    if (data.products.length > 0) {
      let url: keyof InvoiceInfoData, number: keyof InvoiceInfoData;
      let documentType: string;
      setLoading((prev) => ({ ...prev, [type]: true }));
      try {
        if (type === "invoice") {
          url = "invoiceUrl";
          number = "invoiceNo";
          documentType = "INVOICE";
        } else if (type === "deliveryNote") {
          url = "deliveryNoteUrl";
          number = "deliveryNoteNumber";
          documentType = "DELNOTE";
        }
        if (type === "orderConfirmation") {
          documentType = "ORDCONF";
          await downloadFile(`${data.orderCnfDocumentUrl}?id=${data.soNumber}&type=${documentType}`);
        } else {
          const seenFilesDocNo = new Set<string>();
          const downloadPromises: Promise<unknown>[] = [];
          data.products.forEach((item) => {
            const handleUniqueDownload = (documentUrl: string, documentNumber: string) => {
              if (documentUrl && documentNumber && !seenFilesDocNo.has(documentNumber)) {
                seenFilesDocNo.add(documentNumber);
                downloadPromises.push(downloadFile(`${documentUrl}?id=${documentNumber}&type=${documentType}`));
              }
            };
            if (item.invoiceData) {
              handleUniqueDownload(item.invoiceData[url] as string, item.invoiceData[number] as string);
            }
            item.subEntries?.forEach((subItem) => {
              if (subItem.invoiceData) {
                handleUniqueDownload(subItem.invoiceData[url] as string, subItem.invoiceData[number] as string);
              }
            });
          });
          await Promise.all(downloadPromises);
        }
      } catch (error) {
        console.error("Download failed", error);
      } finally {
        setLoading((prev) => ({ ...prev, [type]: false }));
      }
    }
  };

  const haveInvoice = data.products.some(
    (item) => item.invoiceData?.invoiceUrl || item.subEntries?.some((i) => i.invoiceData?.invoiceUrl),
  );
  const haveDeliveryNote = data.products.some(
    (item) => item.invoiceData?.deliveryNoteUrl || item.subEntries?.some((i) => i.invoiceData?.invoiceUrl),
  );
  const haveOrderCnfDocument = (data.orderCnfDocumentUrl?.length ?? 0) > 0;

  const isDownloadableStatus = ["H3", "H4", "H5", "H6"].includes(data.orderStatusCode);

  return (
    <OdHeader>
      <Typography variant="headlineLarge" fontWeight="bold">
        {t("orderDetailsPage.title")}
      </Typography>
      {isDownloadableStatus && (haveInvoice || haveDeliveryNote || haveOrderCnfDocument) && (
        <>
          <Button
            color="secondary"
            size={"medium"}
            onClick={handleClick}
            endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          >
            {t("orderDetailsPage.summary.download.btn.title")}
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ component: StyledMenuPaper }}
          >
            <Stack sx={{ gap: 1, minWidth: 275 }}>
              {haveInvoice && (
                <MenuItem onClick={() => handleDocumentDownload("invoice")}>
                  <FileDownloadOutlinedIcon sx={{ marginRight: "16px" }} />
                  {t("orderDetailsPage.summary.download.invoices")}
                </MenuItem>
              )}

              {haveDeliveryNote && (
                <MenuItem onClick={() => handleDocumentDownload("deliveryNote")}>
                  <FileDownloadOutlinedIcon sx={{ marginRight: "16px" }} />
                  {t("orderDetailsPage.summary.download.deliveryNotes")}
                </MenuItem>
              )}

              {haveOrderCnfDocument && (
                <MenuItem onClick={() => handleDocumentDownload("orderConfirmation")}>
                  <FileDownloadOutlinedIcon sx={{ marginRight: "16px" }} />
                  {t("orderDetailsPage.summary.download.orderConfirmation")}
                </MenuItem>
              )}
            </Stack>
          </Menu>
        </>
      )}
    </OdHeader>
  );
};

export default OrderDetailsHeaderV2;

const OdHeader = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
});

const StyledMenuPaper = styled(Paper)(({ theme }) => ({
  marginTop: "8px !important",
  width: "auto",
  borderRadius: "4px",
  boxShadow: theme.shadows[2],
  backgroundColor: theme.palette.background.default,
}));
